<script setup lang="ts">
const { LocalConfigurationStore } = stores()

const banner = computed(() => LocalConfigurationStore.value?.siteInfo?.banner)
const { $viewport } = useNuxtApp()
const { desktop, mobile } = banner.value?.src ?? {}

const bannerImg = computed(() => {
  if ($viewport.isGreaterOrEquals('md')) return desktop ?? mobile
  else return mobile ?? desktop
})

const bannerImgRef = ref<string[]>(['banner-img'])
const { allCompleted } = useImageLoaderTracker(bannerImgRef)

const isReady = computed(() => allCompleted.value)
</script>

<template>
  <div class="w-full">
    <div
      v-if="!isReady"
      class="aspect-[5/7] overflow-hidden w-full"
      :class="{
        ' md:aspect-[5/2] rounded-xl': !banner?.wide,
        'md:h-[400px]': banner?.wide,
      }"
    >
      <PvSkeleton class="aspect-[1/2] w-full" />
    </div>
    <ClientOnly>
      <div
        v-show="isReady"
        class="aspect-[5/7] relative size-full flex justify-center items-center bg-white overflow-hidden"
        :class="{
          'rounded-xl shadow-md   md:aspect-[5/2]': !banner?.wide,
          'md:h-[400px]': banner?.wide,
        }"
      >
        <template v-if="banner?.static">
          <ImageComponent
            v-show="isReady"
            id="banner-img"
            :src="getSiteAsset({ type: 'img', path: 'banner', fileName: bannerImg })"
            class="w-full h-full object-cover"
            :class="{
              'cursor-pointer': !!banner.url,
              rounded: !banner?.wide,
            }"
            @click="!!banner.url ? navigateTo(banner.url, { external: true }) : undefined"
          />
        </template>
        <template v-else>
          <LayoutContainer
            v-show="isReady"
            class="w-full z-10 h-full md:h-min"
          >
            <div class="flex flex-col justify-between md:flex-row gap-4 p-4 md:p-0 h-full">
              <div
                class="text-primary flex flex-col gap-4 md:gap-8 justify-between size-full md:w-1/2 md:order-first order-last"
              >
                <div
                  v-if="banner?.heading"
                  class="font-bold text-2xl md:text-4xl leading-none"
                >
                  {{ banner?.heading }}
                </div>
                <div class="text-sm">
                  {{ banner?.body }}
                </div>
                <div class="flex gap-2 flex-col md:flex-row">
                  <template
                    v-for="cta in banner?.callToActions"
                    :key="cta.label"
                  >
                    <PvButton
                      :href="cta.link"
                      as="a"
                      :class="{
                        'hover:text-primary': !cta.main,
                      }"
                      :severity="!cta.main ? 'secondary' : undefined"
                    >
                      {{ cta.label }}
                    </PvButton>
                  </template>
                </div>
              </div>
              <div class="md:w-1/2 flex justify-center items-center">
                <ImageComponent
                  id="banner-img"
                  :src="
                    getSiteAsset({
                      type: 'img',
                      path: 'banner',
                      fileName: bannerImg,
                    })
                  "
                  class="object-cove rounded"
                />
              </div>
            </div>
          </LayoutContainer>
        </template>
      </div>
    </ClientOnly>
  </div>
</template>
